"use client"

import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Skeleton from "@mui/material/Skeleton"
import { NextImage, Icons } from "@src/components"
import { NO_IMAGE } from "@src/components/NextImage"
import useDomLoaded from "@src/hooks/use-dom-loaded"
import { Swiper, SwiperSlide } from "swiper/react"
import type { SwiperRef } from "swiper/react"
import { makeStyles } from "tss-react/mui"
import { Navigation } from "swiper/modules"
import { useRef, useEffect, useState, useMemo } from "react"
import ProductModel from "@src/models/product"
import ProductImageLightbox from "./product-image-lightbox"
import { uniqueBy, urlPathname } from "@src/utils/unique"
import { getProductGalleryImages, getProductVideo } from "./gallery-constants"

const VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".ogg", ".avi", ".mkv"]

function isVideoUrl(url: string): boolean {
  const pathname = urlPathname(url).toLowerCase()
  return VIDEO_EXTENSIONS.some(ext => pathname.endsWith(ext))
}

const useStyles = makeStyles()(() => ({
  paginationBulletActive: {
    boxSizing: "border-box",
    background: "black",
    width: "12px",
    height: "12px"
  },
  mySwiper: {
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    "& .swiper-wrapper ": {
      height: "100%"
    }
  }
}))

interface ImageCarouselProps {
  name: string
  isLoading: boolean
  isModal?: boolean
  detail: ProductModel
  selectedImage?: string
}

const ImageCarousel = ({
  selectedImage,
  isModal,
  isLoading,
  name,
  detail
}: ImageCarouselProps) => {
  const isDomLoaded = useDomLoaded()
  const { classes } = useStyles()
  const swiperRef = useRef<SwiperRef>(null)

  const [isEnd, setIsEnd] = useState(false)
  const [isBeginning, setIsBeginning] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const imageList: string[] = useMemo(() => {
    if (!detail?.id) return []
    const images = getProductGalleryImages(detail)
    const attributeImages = (detail.attributes?.map(item => item?.image) || []).filter(
      (image): image is string => Boolean(image)
    )
    return uniqueBy<string>([...images, ...attributeImages], urlPathname)
  }, [detail?.id, detail?.images, detail?.attributes, detail?.size_image, detail?.size_images, detail?.video])

  const firstSrc = imageList[0] || NO_IMAGE
  const isFirstVideo = isVideoUrl(firstSrc)
  const posterImage = !isFirstVideo
    ? undefined
    : detail?.video_thumbnail || imageList.find(img => !isVideoUrl(img)) || undefined

  useEffect(() => {
    if (!selectedImage || !imageList.length || !swiperRef.current?.swiper) return
    const index = imageList.indexOf(selectedImage)
    if (index >= 0) swiperRef.current.swiper.slideTo(index)
  }, [selectedImage, imageList])

  const syncNavState = (swiper?: { isEnd: boolean; isBeginning: boolean } | null) => {
    if (!swiper) return
    setIsEnd(prev => (prev !== swiper.isEnd ? swiper.isEnd : prev))
    setIsBeginning(prev => (prev !== swiper.isBeginning ? swiper.isBeginning : prev))
  }

  const handleSwiper = (swiper?: { isEnd: boolean; isBeginning: boolean } | null) => {
    if (!swiper) return
    queueMicrotask(() => {
      syncNavState(swiper)
    })
  }

  if (isLoading) {
    return (
      <Skeleton
        sx={{
          marginTop: 1,
          borderRadius: "8px",
          minHeight: isModal ? "120px" : "480px",
          transform: "none"
        }}
        variant="rectangular"
        animation="wave"
      />
    )
  }

  // Paint LCP image before Swiper hydrates so the main photo starts loading with the page.
  if (!isDomLoaded) {
    return (
      <Box
        sx={{
          marginTop: 1,
          position: "relative",
          height: isModal ? "120px" : "480px",
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        {isFirstVideo ? (
          <video
            src={firstSrc}
            autoPlay
            muted
            loop
            playsInline
            poster={posterImage}
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, position: "relative", zIndex: 1 }}
          />
        ) : (
          <NextImage
            src={firstSrc}
            alt={name}
            priority
            sizes={isModal ? "120px" : "100vw"}
            sx={{
              position: "relative",
              zIndex: 1,
              width: isModal ? "120px !important" : "100% !important",
              height: isModal ? "120px !important" : "100% !important",
              "& .next-image-full": {
                objectFit: "cover",
                objectPosition: "top"
              }
            }}
          />
        )}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        marginTop: 1,
        position: "relative",
        height: isModal ? "120px" : "480px"
      }}
    >
      {!isModal && (
        <Stack
          justifyContent="center"
          sx={{
            zIndex: 99,
            position: "absolute",
            top: "50%",
            left: 5,
            transform: "translateY(-50%)"
          }}
        >
          <Icons
            sx={{
              background: "rgba(255, 255, 255, 0.8)",
              "&:disabled": { background: "rgba(255, 255, 255, 0.3)" }
            }}
            name="caret-left"
            type="button"
            disabled={isBeginning}
            onClick={() => swiperRef.current?.swiper?.slidePrev()}
          />
        </Stack>
      )}

      {imageList.length > 0 && (
        <Swiper
          ref={swiperRef}
          spaceBetween={10}
          slidesPerView={isModal ? "auto" : 1}
          pagination={{
            clickable: true,
            bulletActiveClass: classes.paginationBulletActive
          }}
          className={classes.mySwiper}
          modules={[Navigation]}
          onSwiper={handleSwiper}
          onSlideChange={syncNavState}
        >
          {imageList.map((item, index) => {
            const itemIsVideo = isVideoUrl(item)
            const itemPoster = itemIsVideo
              ? (detail as any)?.video_thumbnail || imageList.find(img => !isVideoUrl(img))
              : undefined
            return (
            <SwiperSlide key={item} style={isModal ? { width: "auto" } : undefined}>
              <Box
                onClick={() => {
                  if (isModal || itemIsVideo) return
                  setLightboxIndex(index)
                  setLightboxOpen(true)
                }}
                sx={{
                  position: "relative",
                  height: "100%",
                  cursor: isModal || itemIsVideo ? "default" : "zoom-in",
                  borderRadius: "8px",
                  overflow: "hidden"
                }}
              >
                {itemIsVideo ? (
                  <video
                    src={item}
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={itemPoster}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <NextImage
                    src={item}
                    alt={name}
                    priority={index === 0}
                    sizes={isModal ? "120px" : "100vw"}
                    sx={{
                      width: isModal ? "120px !important" : "100% !important",
                      height: isModal ? "120px !important" : "100% !important",
                      "& .next-image-full": {
                        objectFit: "cover",
                        objectPosition: "top"
                      }
                    }}
                  />
                )}
                {!isModal && !itemIsVideo && (
                  <Box
                    sx={{
                      position: "absolute",
                      right: 12,
                      bottom: 12,
                      zIndex: 2,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.9)",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                      pointerEvents: "none"
                    }}
                  >
                    <Icons name="search" />
                  </Box>
                )}
              </Box>
            </SwiperSlide>
            )
          })}
        </Swiper>
      )}

      {!isModal && (
        <>
          <ProductImageLightbox
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            src={imageList[lightboxIndex] || ""}
            alt={name}
            images={imageList.filter(src => !isVideoUrl(src))}
            initialIndex={lightboxIndex}
          />
          <Stack
            justifyContent="center"
            sx={{
              zIndex: 99,
              position: "absolute",
              top: "50%",
              right: 5,
              transform: "translateY(-50%)"
            }}
          >
            <Icons
              name="caret-right"
              type="button"
              sx={{
                background: "rgba(255, 255, 255, 0.8)",
                "&:disabled": { background: "rgba(255, 255, 255, 0.3)" }
              }}
              disabled={isEnd}
              onClick={() => swiperRef.current?.swiper?.slideNext()}
            />
          </Stack>
        </>
      )}
    </Box>
  )
}

export default ImageCarousel
